import { FC, useContext, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled, { css, keyframes } from 'styled-components';

import PrimaryButton from '@/components/Buttons/PrimaryButton';
import ContainerSmall from '@/components/ContainerSmall/ContainerSmall';
import { getImage } from '@/lib/fetchAPI';
import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

import AdvantageItem from './AdvantageItem';

const Advantages: FC<any> = ({ data }) => {
	const { lang } = useContext(GlobalContext);
	const [activeSlideIndex, setActiveSlideIndex] = useState(0);
	const [touchStart, setTouchStart] = useState(0);
	const [touchEnd, setTouchEnd] = useState(0);

	const prevSlideIndex = (activeSlideIndex ?? data?.length ?? 1) - 1;

	const prevSlide = () =>
		setActiveSlideIndex((current) =>
			current ? activeSlideIndex - 1 : data?.items.length - 1,
		);

	const nextSlide = () =>
		setActiveSlideIndex((current) =>
			current === data?.advantage.length - 1 ? 0 : activeSlideIndex + 1,
		);

	const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);

	const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

	const handleTouchEnd = () => {
		if (touchStart - touchEnd > 80) nextSlide();

		if (touchStart - touchEnd < -80) prevSlide();
	};

	return (
		<Section>
			<ContainerSmall>
				<div>
					<Heading>
						<div>
							<ScrollAnimation
								animateOnce
								animateIn="fadeInUp"
								duration={0.76}
								offset={150}
							>
								<Subtitle>
									{translate(lang, { en: 'The', ru: ' ', ua: ' ' })}
								</Subtitle>

								<Title $lang={lang}>{data?.title}</Title>
							</ScrollAnimation>
						</div>

						<div>
							<ScrollAnimation
								animateOnce
								offset={150}
								duration={0.76}
								animateIn="fadeIn"
							>
								<Image
									loading="lazy"
									alt={data?.imageAlt}
									src={getImage(data?.image)}
								/>
							</ScrollAnimation>
						</div>
					</Heading>

					<MobileSliderWrapper>
						<AdvantagesMobileSlider>
							<AdvantagesRow
								$lang={lang}
								onTouchEnd={() => handleTouchEnd()}
								onTouchMove={(e) => handleTouchMove(e)}
								onTouchStart={(e) => handleTouchStart(e)}
							>
								<Gradient
									loading="lazy"
									alt="Gradient"
									src="/images/gradient.png"
								/>

								{data?.items?.map((advantage, index) => (
									<AdvantageCol
										key={`advantage_slide_${index}`}
										className={
											index === activeSlideIndex
												? 'active'
												: index === prevSlideIndex
													? 'prev'
													: 'next'
										}
									>
										<ScrollAnimation
											animateOnce
											duration={1}
											offset={150}
											animateIn="fadeInUp"
											delay={95 * ((index % 2) + 1)}
										>
											<AdvantageItem index={index} advantage={advantage} />
										</ScrollAnimation>
									</AdvantageCol>
								))}

								<YellowGrad
									loading="lazy"
									alt="Gradient"
									src="/images/yellowgrad.png"
								/>
							</AdvantagesRow>

							<ArrowLeft onClick={prevSlide}>
								<img alt="Previous" src="/images/icons/slider/left-arrow.svg" />
							</ArrowLeft>

							<ArrowRight onClick={nextSlide}>
								<img alt="Next" src="/images/icons/slider/right-arrow.svg" />
							</ArrowRight>
						</AdvantagesMobileSlider>

						<Dots>
							{data?.items?.map((_, index) => (
								<Dot
									key={`advantage_dot_${index}`}
									$active={index === activeSlideIndex}
									onClick={() => setActiveSlideIndex(index)}
								/>
							))}
						</Dots>
					</MobileSliderWrapper>

					<ButtonWrapper>
						<ScrollAnimation animateOnce animateIn="fadeInUp" duration={0.8}>
							<PrimaryButton
								width="wide"
								href="/viber-for-business"
								text={translate(lang, {
									en: 'Learn more',
									ru: 'Узнать больше',
									ua: 'Дізнайтеся більше',
								})}
							/>
						</ScrollAnimation>
					</ButtonWrapper>
				</div>
			</ContainerSmall>
		</Section>
	);
};

const Gradient = styled.img`
	position: absolute;
	filter: blur(100px);
	z-index: 10;

	@media (max-width: 840px) {
		display: none;
	}
`;

const YellowGrad = styled.img`
	position: absolute;
	filter: blur(100px);
	top: 50%;
	z-index: 10;

	@media (max-width: 840px) {
		display: none;
	}
`;

const Section = styled.section`
	padding-bottom: 135px;
	position: relative;
	overflow: hidden;

	@media (max-width: 600px) {
		padding-bottom: 74px;
	}
`;

const Heading = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 15px;

	@media (max-width: 833px) {
		margin-bottom: 36px;
	}

	@media (max-width: 600px) {
		padding-left: 15px;
	}
`;

const Subtitle = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	color: var(--color-purple);
	margin-top: 30px;

	@media (max-width: 600px) {
		font-size: 12px;
		line-height: 15px;
		margin-top: 0;
	}
`;

const Title = styled.h2<{ $lang: string }>`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	color: var(--color-purple);

	${({ $lang }) =>
		$lang === 'ru'
			? css`
					letter-spacing: 2px;
					line-height: 70px;
				`
			: $lang === 'ua'
				? css`
						letter-spacing: 2px;
						max-width: 400px;
					`
				: null};

	@media (max-width: 1000px) {
		font-size: 58px;
	}

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 40px;
		max-width: 210px;
	}
`;

const Image = styled.img`
	display: block;
	border-radius: 30px;
	max-width: 450px;

	@media (max-width: 1000px) {
		max-width: 390px;
	}

	@media (max-width: 780px) {
		max-width: 300px;
	}

	@media (max-width: 670px) {
		display: none;
	}

	@media (max-width: 600px) {
		display: none;
	}
`;

const MobileSliderWrapper = styled.div`
	@media (max-width: 833px) {
		padding: 0 15px;
	}
`;

const AdvantagesMobileSlider = styled.div`
	@media (max-width: 833px) {
		max-width: 420px;
		width: 100%;
		margin: 0 auto;
		position: relative;
	}

	@media (max-width: 600px) {
		max-width: 375px;
	}
`;

const AdvantageCol = styled.div`
	width: calc(50% - 30px);
	margin-bottom: 40px;
	z-index: 15;

	.animated {
		height: 100%;
	}

	@media (max-width: 833px) {
		width: 100%;
		height: 100%;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		position: absolute;

		&.active {
			transition: transform 0.4s;
		}
		&.prev {
			transform: translateX(-100%);
		}
		&.next {
			transform: translateX(100%);
		}
	}
`;

const AdvantagesRow = styled.div<{ $lang: string }>`
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	flex-wrap: wrap;
	margin-top: 60px;
	margin-bottom: 20px;
	position: relative;

	@media (max-width: 833px) {
		width: 100%;
		margin: 0 auto 48px;
		min-height: 340px;
		overflow: hidden;
		flex-wrap: nowrap;
		align-items: center;
	}

	@media (max-width: 600px) {
		width: 100%;
		min-height: 250px;

		${({ $lang }) =>
			$lang === 'ua' &&
			css`
				min-height: 320px;
			`}
	}

	${AdvantageCol}:last-child {
		h3 {
			max-width: 160px;
		}
	}
`;

export const arrowPulsing = keyframes`
  from {
    transform: scale(1.1);
  }

  50% {
    transform: scale(1.25);
  }
  
  to {
    transform: scale(1.1);
  }
`;

const Arrow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	width: 50px;
	height: 50px;
	top: 230px;
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	border-radius: 50%;
	cursor: pointer;
	visibility: hidden;

	@media (max-width: 833px) {
		visibility: visible;
		top: 50%;
		z-index: 99;
	}

	img {
		animation-name: ${arrowPulsing};
		animation-duration: 1.7s;
		animation-iteration-count: infinite;
		animation-fill-mode: both;
		animation-timing-function: linear;
		animation-play-state: running;
	}
`;

const ArrowLeft = styled(Arrow)`
	left: -3%;
`;

const ArrowRight = styled(Arrow)`
	right: -3%;
`;

const Dots = styled.div`
	display: none;

	@media (max-width: 833px) {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 42px;
	}
`;

const Dot = styled.div<{ $active?: boolean }>`
	width: 10px;
	height: 10px;
	margin-left: 8px;
	margin-right: 8px;
	position: relative;
	cursor: pointer;

	&:after {
		content: '';
		position: absolute;
		width: 4px;
		height: 4px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		background-color: var(--color-purple);
		border-radius: 50%;
		transition: 0.3s;

		${({ $active }) =>
			$active &&
			css`
				width: 10px;
				height: 10px;
			`}
	}
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 60px;
	position: relative;
	z-index: 30;

	@media (max-width: 833px) {
		margin-top: 0;
	}

	& > div {
		width: 100%;
		padding: 0 15px;
		text-align: center;
	}
`;

export default Advantages;
